import React, { useState, useEffect, useMemo } from 'react';
import { locationsAPI, warehousesAPI, maestrosAPI } from '@/lib/api';
import { Plus, Pencil, Trash2, MapPin, Warehouse, Tag, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const AdminLocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [maestros, setMaestros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    phone: '',
    warehouse_id: '',
    maestros: [],
    order_type: 'cocina',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [locationsRes, warehousesRes, maestrosRes] = await Promise.all([
        locationsAPI.getAll(false),
        warehousesAPI.getActive(),
        maestrosAPI.getAll(true)
      ]);
      setLocations(locationsRes.data);
      setWarehouses(warehousesRes.data);
      setMaestros(maestrosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Helper para obtener el nombre de la bodega
  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return 'Sin asignar';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse?.name || 'Desconocida';
  };

  // Helper para obtener nombres de maestros
  // Ahora maestros son nombres directamente, no IDs
  const getMaestrosNames = (maestroNames) => {
    if (!maestroNames || maestroNames.length === 0) return 'Todos los productos';
    return maestroNames.join(', ');
  };

  // Calcular locales sin bodega asignada
  const locationsWithoutWarehouse = useMemo(() => {
    return locations.filter(loc => !loc.warehouse_id);
  }, [locations]);

  // Toggle maestro selection - usamos el nombre del maestro para facilitar el matching
  const toggleMaestro = (maestroName) => {
    setFormData(prev => ({
      ...prev,
      maestros: prev.maestros.includes(maestroName)
        ? prev.maestros.filter(name => name !== maestroName)
        : [...prev.maestros, maestroName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        warehouse_id: formData.warehouse_id || null,
        maestros: formData.maestros.length > 0 ? formData.maestros : []
      };
      if (editingLocation) {
        await locationsAPI.update(editingLocation.id, dataToSend);
        toast.success('Local actualizado');
      } else {
        await locationsAPI.create(dataToSend);
        toast.success('Local creado');
      }
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar local');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este local?')) {
      try {
        await locationsAPI.delete(id);
        toast.success('Local eliminado');
        loadData();
      } catch (error) {
        toast.error('Error al eliminar local');
      }
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingLocation(null);
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      code: location.code,
      name: location.name,
      address: location.address || '',
      phone: location.phone || '',
      warehouse_id: location.warehouse_id || '',
      maestros: location.maestros || [],
      order_type: location.order_type || 'cocina',
      is_active: location.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      address: '',
      phone: '',
      warehouse_id: '',
      maestros: [],
      order_type: 'cocina',
      is_active: true
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-locations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Locales</h1>
          <p className="text-muted-foreground mt-1">Gestiona los locales/sucursales</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-location-button">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Local
        </Button>
      </div>

      {/* Alerta de locales sin bodega */}
      {locationsWithoutWarehouse.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="text-amber-800 font-semibold">
                ⚠️ {locationsWithoutWarehouse.length} local(es) sin bodega asignada
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Los pedidos e inventarios de estos locales no aparecerán correctamente en los filtros por bodega. 
                Haz clic en "Editar" para asignarles una bodega:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {locationsWithoutWarehouse.map(loc => (
                  <span key={loc.id} className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                    <MapPin className="w-3 h-3 mr-1" />
                    {loc.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Código</th>
                <th className="px-6 py-3 text-left font-semibold">Nombre</th>
                <th className="px-6 py-3 text-left font-semibold">Tipo</th>
                <th className="px-6 py-3 text-left font-semibold">Bodega</th>
                <th className="px-6 py-3 text-left font-semibold">Maestros/Sub-Pedidos</th>
                <th className="px-6 py-3 text-center font-semibold">Estado</th>
                <th className="px-6 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.id} className="border-b hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 font-mono text-sm">{location.code}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-slate-900">{location.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      location.order_type === 'barra' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {location.order_type === 'barra' ? 'Barra' : 'Cocina'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Warehouse className="w-4 h-4 text-slate-400" />
                      <span className={`text-sm ${location.warehouse_id ? 'text-slate-700' : 'text-amber-600 font-medium'}`}>
                        {getWarehouseName(location.warehouse_id)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600 max-w-[200px] truncate" title={getMaestrosNames(location.maestros)}>
                        {getMaestrosNames(location.maestros)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      location.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {location.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(location)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(location.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Editar Local' : 'Nuevo Local'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="LOC-001"
                  required
                />
              </div>
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Local Centro"
                  required
                />
              </div>
              <div>
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Calle Principal 123"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+56912345678"
                />
              </div>
              <div>
                <Label htmlFor="warehouse">Bodega Asignada</Label>
                <Select
                  value={formData.warehouse_id}
                  onValueChange={(value) => setFormData({ ...formData, warehouse_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger id="warehouse" data-testid="location-warehouse-select">
                    <SelectValue placeholder="Seleccionar bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar (Bodega Central)</SelectItem>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Los locales sin bodega asignada serán atendidos por Bodega Central
                </p>
              </div>
              
              {/* Tipo de Pedido */}
              <div>
                <Label htmlFor="order_type">Tipo de Pedido</Label>
                <Select
                  value={formData.order_type}
                  onValueChange={(value) => setFormData({ ...formData, order_type: value })}
                >
                  <SelectTrigger id="order_type" data-testid="location-order-type-select">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cocina">Cocina</SelectItem>
                    <SelectItem value="barra">Barra</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Los pedidos de "Barra" y "Cocina" aparecen separados en el consolidado
                </p>
              </div>
              
              {/* Maestros/Sub-Pedidos */}
              <div>
                <Label className="mb-2 block">Maestros / Sub-Pedidos</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Selecciona los maestros para filtrar qué productos puede ver este local.
                  Si no seleccionas ninguno, verá todos los productos.
                </p>
                <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {maestros.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">
                      No hay maestros creados
                    </p>
                  ) : (
                    maestros.map((maestro) => (
                      <div key={maestro.id} className="flex items-center gap-3">
                        <Checkbox
                          id={`maestro-${maestro.id}`}
                          checked={formData.maestros.includes(maestro.name)}
                          onCheckedChange={() => toggleMaestro(maestro.name)}
                        />
                        <label 
                          htmlFor={`maestro-${maestro.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {maestro.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.maestros.length > 0 && (
                  <p className="text-xs text-blue-600 mt-2">
                    {formData.maestros.length} maestro(s) seleccionado(s)
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingLocation ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLocationsPage;